import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const projectsData = [
  {
    id: 1,
    name: "FIRST Group",
    year: "2024",
    category: "Technology & Real Estate",
    role: "Founder & CEO",
    description: "Technology-driven real estate and operational infrastructure group serving international investors in Bali.",
    link: "https://first.co.id",
    linkLabel: "Visit First.co.id",
    logo: "/images/first-group.svg",
  },
  {
    id: 2,
    name: "FIRST Property",
    year: "2024",
    category: "Real Estate",
    role: "Founder",
    description: "Real estate investment and property services in Bali. Full-service property management and investment advisory.",
    link: "https://firstproperty.id",
    linkLabel: "Visit FirstProperty.id",
    logo: "/images/first-property.svg",
  },
  {
    id: 3,
    name: "Mac Expert",
    year: "2016",
    category: "Apple Infrastructure",
    role: "Founder",
    description: "Apple infrastructure consulting and advanced technical services for enterprises and professionals.",
    links: [
      { url: "https://macexpert.fr", label: "MacExpert.FR" },
      { url: "https://macexpert.ae", label: "MacExpert.AE" },
    ],
    logo: "/images/mac-expert.png",
  },
  {
    id: 4,
    name: "Koosmik",
    year: "2021",
    category: "Fintech",
    role: "COO",
    description: "Digital payments infrastructure and fintech solutions for emerging markets.",
    logo: "/images/koosmik.webp",
    invertLogo: true,
  },
  {
    id: 5,
    name: "Apple CEMEA",
    year: "2005",
    category: "Technology",
    role: "Africa Area Manager",
    description: "Regional leadership overseeing Apple ecosystem expansion across CEMEA region.",
    logo: "/images/apple.png",
  },
];

export const Projects = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      cardsRef.current.forEach((card, index) => {
        gsap.from(card, {
          y: 100,
          opacity: 0,
          scale: 0.95,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.1
        });

        const logo = card.querySelector('.project-logo');
        gsap.from(logo, {
          scale: 0.8,
          opacity: 0,
          duration: 0.6,
          ease: "back.out(1.5)",
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            toggleActions: "play none none reverse"
          },
          delay: 0.2 + index * 0.1
        });

        const arrow = card.querySelector('.arrow-icon');
        
        card.addEventListener('mouseenter', () => {
          gsap.to(card, { scale: 1.02, duration: 0.3, ease: "power2.out" });
          gsap.to(arrow, { x: 5, y: -5, duration: 0.3, ease: "power2.out" });
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(card, { scale: 1, duration: 0.3, ease: "power2.out" });
          gsap.to(arrow, { x: 0, y: 0, duration: 0.3, ease: "power2.out" });
        });
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="projects"
      data-testid="projects-section"
      className="py-24 md:py-32 bg-obsidian border-t border-border/50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div ref={headerRef} className="mb-16 md:mb-20">
          <span className="text-xs uppercase tracking-[0.3em] text-concrete/60 font-medium block mb-4">
            04
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-concrete">
            Ventures & Companies
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectsData.map((project, index) => (
            <div
              key={project.id}
              ref={(el) => cardsRef.current[index] = el}
              data-testid={`project-item-${index}`}
              className="card-3d group block border border-concrete/20 p-6 hover:bg-concrete/5 transition-colors duration-300 cursor-pointer"
            >
              <div className="project-logo h-16 mb-6 flex items-center">
                <img
                  src={project.logo}
                  alt={project.name}
                  className={`h-full w-auto max-w-full object-contain ${project.invertLogo ? 'invert' : ''}`}
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-3 text-xs text-concrete/50 uppercase tracking-wider">
                <span>{project.year}</span>
                <span>•</span>
                <span>{project.role}</span>
              </div>

              <p className="text-concrete/70 text-sm leading-relaxed mb-4">
                {project.description}
              </p>

              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-concrete font-medium uppercase tracking-wider hover:text-concrete/70 transition-colors"
                >
                  {project.linkLabel}
                  <ArrowUpRight className="arrow-icon w-3 h-3" />
                </a>
              )}
              {project.links && (
                <div className="flex flex-wrap gap-4">
                  {project.links.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-concrete font-medium uppercase tracking-wider hover:text-concrete/70 transition-colors"
                    >
                      {link.label}
                      <ArrowUpRight className="arrow-icon w-3 h-3" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
